import { useMemo } from 'react';
import { useIndodaxWebSocket } from './useIndodaxWebSocket';
import {
  convertIdrToAsetqu,
  convertAsetquToIdr,
  formatAsetquAmount,
  formatIdrAmount,
} from '@/lib/utils';
/**
 * Hook untuk konversi IDR ke ASETQU menggunakan harga real-time dari Indodax WebSocket
 * @returns Object dengan fungsi convert dan format, serta data harga real-time
 */
export const useIdrToAsetqu = () => {
  const { priceIdr, isConnected, isLoadingInitial, error } = useIndodaxWebSocket();

  const convert = useMemo(
    () => (idrAmount: number) => {
      return convertIdrToAsetqu(idrAmount, priceIdr);
    },
    [priceIdr]
  );

  const convertAndFormat = useMemo(
    () => (idrAmount: number) => {
      const tokenAmount = convertIdrToAsetqu(idrAmount, priceIdr);
      return formatAsetquAmount(tokenAmount);
    },
    [priceIdr]
  );

  return {
    /**
     * Konversi IDR ke ASETQU token
     * @param idrAmount - Jumlah dalam IDR
     * @returns Jumlah ASETQU token (raw number)
     */
    convert,

    /**
     * Konversi IDR ke ASETQU token dan format hasilnya
     * @param idrAmount - Jumlah dalam IDR
     * @returns String terformat dengan desimal yang sesuai
     */
    convertAndFormat,

    /**
     * Harga ASETQU saat ini dalam IDR
     */
    priceIdr,

    /**
     * Status koneksi WebSocket
     */
    isConnected,

    /**
     * Loading state saat mengambil harga awal
     */
    isLoadingInitial,

    /**
     * Error jika ada
     */
    error,
  };
};

/**
 * Hook untuk konversi ASETQU ke IDR menggunakan harga real-time dari Indodax WebSocket
 * @returns Object dengan fungsi convert dan format, serta data harga real-time
 */
export const useAsetquToIdr = () => {
  const { priceIdr, isConnected, isLoadingInitial, error } = useIndodaxWebSocket();

  const convert = useMemo(
    () => (tokenAmount: number) => {
      return convertAsetquToIdr(tokenAmount, priceIdr);
    },
    [priceIdr]
  );

  const convertAndFormat = useMemo(
    () => (tokenAmount: number) => {
      const idrAmount = convertAsetquToIdr(tokenAmount, priceIdr);
      return formatIdrAmount(idrAmount);
    },
    [priceIdr]
  );

  return {
    /**
     * Konversi ASETQU token ke IDR
     * @param tokenAmount - Jumlah dalam ASETQU token
     * @returns Jumlah dalam IDR (raw number)
     */
    convert,

    /**
     * Konversi ASETQU token ke IDR dan format hasilnya
     * @param tokenAmount - Jumlah dalam ASETQU token
     * @returns String terformat dengan prefix "Rp" dan thousand separators
     */
    convertAndFormat,

    /**
     * Harga ASETQU saat ini dalam IDR
     */
    priceIdr,

    /**
     * Status koneksi WebSocket
     */
    isConnected,

    /**
     * Loading state saat mengambil harga awal
     */
    isLoadingInitial,

    /**
     * Error jika ada
     */
    error,
  };
};

/**
 * Hook untuk konversi dua arah (IDR ↔ ASETQU) menggunakan harga real-time dari Indodax WebSocket
 * @returns Object dengan fungsi konversi dua arah dan data harga real-time
 */
export const useCurrencyConversion = () => {
  const { priceIdr, isConnected, isLoadingInitial, error } = useIndodaxWebSocket();

  return {
    /**
     * Konversi IDR ke ASETQU token
     * @param idrAmount - Jumlah dalam IDR
     * @returns Jumlah ASETQU token (raw number)
     */
    idrToAsetqu: (idrAmount: number) => convertIdrToAsetqu(idrAmount, priceIdr),

    /**
     * Konversi ASETQU token ke IDR
     * @param tokenAmount - Jumlah dalam ASETQU token
     * @returns Jumlah dalam IDR (raw number)
     */
    asetquToIdr: (tokenAmount: number) => convertAsetquToIdr(tokenAmount, priceIdr),

    /**
     * Konversi IDR ke ASETQU token dan format hasilnya
     * @param idrAmount - Jumlah dalam IDR
     * @returns String terformat dengan desimal yang sesuai
     */
    idrToAsetquFormatted: (idrAmount: number) => {
      const tokenAmount = convertIdrToAsetqu(idrAmount, priceIdr);
      return formatAsetquAmount(tokenAmount);
    },

    /**
     * Konversi ASETQU token ke IDR dan format hasilnya
     * @param tokenAmount - Jumlah dalam ASETQU token
     * @returns String terformat dengan prefix "Rp" dan thousand separators
     */
    asetquToIdrFormatted: (tokenAmount: number) => {
      const idrAmount = convertAsetquToIdr(tokenAmount, priceIdr);
      return formatIdrAmount(idrAmount);
    },

    /**
     * Harga ASETQU saat ini dalam IDR
     */
    priceIdr,

    /**
     * Status koneksi WebSocket
     */
    isConnected,

    /**
     * Loading state saat mengambil harga awal
     */
    isLoadingInitial,

    /**
     * Error jika ada
     */
    error,
  };
};
